import * as React from 'react';
import classnames from 'classnames';
import sanitizeHtml from 'sanitize-html';

import './overlay.scss';

type Props = {
  parentId: string,
  text?: string,
  children?: React.ReactNode,
  className?: string,
  showOnHover?: boolean,
  showOnFocus?: boolean,
  withNoBackgroundColor?: boolean,
  withDefaultCursor?: boolean
};

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['div', 'span', 'strong'],
  allowedAttributes: {
    div: ['class', 'style'],
    span: ['class', 'style']
  }
};

const MOUSEOVER = 'mouseover';
const MOUSEOUT = 'mouseout';
const FOCUS = 'focus';
const BLUR = 'blur';
const ADD_EVENT_LISTENER = 'addEventListener';
const REMOVE_EVENT_LISTENER = 'removeEventListener';

const Overlay = ({
  parentId,
  text,
  children,
  className = '',
  showOnHover = false,
  showOnFocus = false,
  withNoBackgroundColor = false,
  withDefaultCursor = false
}: Props): JSX.Element => {
  const [isVisible, setIsVisible] = React.useState(!showOnHover);
  let parentElement: Element | undefined | null = null;

  const handleMouseEvent = React.useCallback(event => {
    setIsVisible(event === MOUSEOVER);
  }, []);

  const handleFocusEvent = React.useCallback(event => {
    setIsVisible(event === FOCUS);
  }, []);

  const handleMouseEventListeners = (type: 'add' | 'remove'): void => {
    const types = {
      add: ADD_EVENT_LISTENER,
      remove: REMOVE_EVENT_LISTENER
    };

    [MOUSEOVER, MOUSEOUT].map(event => (
      parentElement && parentElement[types[type]](event, () => handleMouseEvent(event))
    ));
  };

  const handleFocusEventListeners = (type: 'add' | 'remove'): void => {
    const types = {
      add: ADD_EVENT_LISTENER,
      remove: REMOVE_EVENT_LISTENER
    };

    [FOCUS, BLUR].map(event => (
      parentElement && parentElement[types[type]](event, () => handleFocusEvent(event))
    ));
  };

  React.useEffect(() => {
    parentElement = document.querySelector(`#${parentId}`);

    if (showOnHover) {
      handleMouseEventListeners('add');
    }

    if (showOnFocus) {
      handleFocusEventListeners('add');
    }

    return () => {
      if (showOnHover) {
        handleMouseEventListeners('remove');
      }

      if (showOnFocus) {
        handleFocusEventListeners('remove');
      }
    };
  }, [handleMouseEvent]);

  const overlayClasses = classnames('overlay', className, {
    'overlay--is-visible': isVisible,
    'overlay--no-background-color': withNoBackgroundColor,
    'overlay--with-default-cursor': withDefaultCursor
  });

  return (
    <div className={overlayClasses}>
      {
        text && !children && (
          <div
            className="overlay__text"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(text, SANITIZE_HTML_CONFIG)
            }}
          />
        )
      }
      {
        children && (
          <div className="overlay__custom-component">
            {children}
          </div>
        )
      }
    </div>
  );
};

export default Overlay;
